import React, { useEffect, useRef } from 'react'
import { useCustomReducer } from '../../../base/customHooks'
import { GetKasperskyProductsApi } from '../services/getKasperskyProductsApi'
import { PostKasperskyProductsApi } from '../services/postKasperskyProductsApi'
import { IKaspersky, IKasperskyProducts, IKasperskyProductsRequest } from '../models/models'
import IPSelectBox, { ISelectOption } from '../../../components/IPSelectBox/IPSelectBox'
import IPInput from '../../../components/IPInput'
import IPButton from '../../../components/IPButton'
import { IAddItemToBasketRequest } from '../../../redux/slices/basket/basketService'
import { addItemToBasketRedux } from '../../../redux/slices/basket/basketSlice'
import { useAppDispatch } from '../../../redux/app/hooks'
import Swal from 'sweetalert2'
import '../style.scss'
import { BaseComponentWithContextProps, componentWithContext } from '../../../base/customHooks/componentHOC'

interface IKasperskyState {
  allData: IKaspersky | null
  filteredProducts: IKasperskyProducts[]
  loading: boolean
  productName: string
  minUser: number
  turn: number
}

const MIN_USER_INPUT_ID = 'kaspersky-min-user-input'

function KasperskyPage(props: BaseComponentWithContextProps) {
  const getInitialState = (): IKasperskyState => {
    return {
      allData: null,
      filteredProducts: [],
      loading: true,
      productName: '',
      minUser: 0,
      turn: 0,
    }
  }

  const initialState = getInitialState()
  const [state, setState] = useCustomReducer<IKasperskyState>(initialState)
  const dispatch = useAppDispatch()
  const filterParamsRef = useRef({ productName: state.productName, minUser: state.minUser, turn: state.turn })
  filterParamsRef.current = { productName: state.productName, minUser: state.minUser, turn: state.turn }

  // İlk yükleme - tüm ürünleri getir
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        props.showLoading()
        setState({ loading: true })
        const getApi = new GetKasperskyProductsApi({})
        const data = await getApi.getKasperskyProducts()
        setState({
          allData: data,
          filteredProducts: [],
          loading: false,
        })
        props.hideLoading()
      } catch (error) {
        props.hideLoading()
        setState({ loading: false })
      }
    }

    fetchInitialData()
  }, [])

  // Filtreleme yap
  const handleFilter = async (productName: string, minUser: number, turn: number) => {
    if (!minUser) {
      // Kullanıcı sayısı girilmeden ürün listesi gösterilmez
      setState({ filteredProducts: [] })
      return
    }

    try {
      props.showLoading()
      setState({ loading: true })
      const request: IKasperskyProductsRequest = {
        PRODUCT_NAME: productName,
        MIN_USER: minUser,
        TURN: turn,
      }

      const postApi = new PostKasperskyProductsApi({})
      const data = await postApi.getFilteredKasperskyProducts(request)
      setState({
        filteredProducts: data,
        loading: false,
      })
      props.hideLoading()
    } catch (error) {
      props.hideLoading()
      setState({ loading: false })
    }
  }

  // Select list seçeneği değiştiğinde
  const handleProductNameChange = (option: ISelectOption) => {
    const newProductName = option?.value as string || ''
    setState({ productName: newProductName })
    handleFilter(newProductName, state.minUser, state.turn)
  }

  // Yazarken sadece görünen değeri günceller, apiye çıkmaz.
  const handleMinUserChange = (e: any) => {
    const newMinUser = e.target.value ? Number(e.target.value) : 0
    setState({ minUser: newMinUser })
  }

  // Kullanıcı input'tan kenara tıkladığında (blur) veya oklarla artırıp/azalttığında
  // tarayıcı native 'change' event'i tetiklenir (her tuş vuruşunda değil), apiye o an çıkılır.
  useEffect(() => {
    const input = document.getElementById(MIN_USER_INPUT_ID) as HTMLInputElement | null
    if (!input) return

    const handleNativeChange = (e: Event) => {
      const target = e.target as HTMLInputElement
      const newMinUser = target.value ? Number(target.value) : 0
      setState({ minUser: newMinUser })
      handleFilter(filterParamsRef.current.productName, newMinUser, filterParamsRef.current.turn)
    }

    input.addEventListener('change', handleNativeChange)
    return () => input.removeEventListener('change', handleNativeChange)
  }, [])

  const handleTurnChange = (option: ISelectOption) => {
    const newTurn = option?.value as number || 0
    setState({ turn: newTurn })
    handleFilter(state.productName, state.minUser, newTurn)
  }

  // Sepete ekle
  const handleAddToBasket = async (product: IKasperskyProducts) => {
    try {
      if (!product.PRODUCT_ID) {
        Swal.fire({
          icon: 'error',
          title: 'Ürün ID bulunamadı',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        return
      }

      if (!state.minUser) {
        Swal.fire({
          icon: 'error',
          title: 'Kullanıcı sayısı bulunamadı',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        return
      }

      const request: IAddItemToBasketRequest = {
        ProductId: product.PRODUCT_ID,
        Quantity: state.minUser,
        IsOppurtunityStock: false,
        OpportunitiyStockId: null,
        IsKaspersky: true,
      }

      props.showLoading()
      const result = await dispatch(addItemToBasketRedux(request)).unwrap()
      props.hideLoading()

      if (result?.description) {
        Swal.fire({
          icon: 'error',
          title: 'Sepete eklenirken hata oluştu:' + result.description,
          showConfirmButton: true,
          allowOutsideClick: false,
        })
        return
      }

      Swal.fire({
        html: `<h5>${product.PRODUCT_NAME} sepetinize eklendi</h5>`,
        icon: 'success',
        confirmButtonText: 'Tamam',
        confirmButtonColor: '#ec4c4c',
      })
    } catch (error) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: 'Sepete eklenirken hata oluştu:' + ((error as any).description || ''),
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  // Kategoriler select list seçenekleri
  const productNameOptions: ISelectOption[] = state.allData?.Categories.map(cat => ({
    value: cat,
    label: cat,
  })) || []



  // Yıl select list seçenekleri
  const turnOptions: ISelectOption[] = state.allData?.Year.map(year => ({
    value: year,
    label: String(year),
  })) || []

  // Seçili değerleri bul
  const selectedProductName = productNameOptions.find(opt => opt.value === state.productName)
  const selectedTurn = turnOptions.find(opt => opt.value === state.turn)

  return (
    <div className='container mt-5'>
      <div className='kaspersky-page'>
        <h1 className='mb-4'>Kaspersky Ürünleri</h1>

        {/* Filtre Seçimleri */}
        <div className='row mb-5'>
          <div className='col-md-4 mb-3'>
            <label className='form-label'>Ürün Adı</label>
            <IPSelectBox
              options={productNameOptions}
              value={selectedProductName}
              onChangeSingle={handleProductNameChange}
              isClearable
              placeholder='Ürün seçiniz'
            />
          </div>

          <div className='col-md-4 mb-3'>
            <label className='form-label'>
              Kullanıcı Sayısı <span style={{ color: 'red' }}>*</span>
            </label>
            <IPInput
              id={MIN_USER_INPUT_ID}
              type='number'
              value={state.minUser || ''}
              onChange={handleMinUserChange}
              placeholder='Kullanıcı sayısı girin'
              min='0'
              required
            />
          </div>

          <div className='col-md-4 mb-3'>
            <label className='form-label'>Yıl</label>
            <IPSelectBox
              options={turnOptions}
              value={selectedTurn}
              onChangeSingle={handleTurnChange}
              isClearable
              placeholder='Yıl seçiniz'
            />
          </div>
        </div>

        {/* Ürünler Tablosu */}
        <div className='table-responsive'>
          <table className='table table-striped table-hover'>
            <thead className='table-dark'>
              <tr>
                <th>Ürün Adı</th>
                <th>Ürün Kodu</th>
                <th>Yıl</th>
                <th>Birim Fiyat(₺)</th>
                <th>Toplam (₺)</th>
                <th>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {state.filteredProducts && state.filteredProducts.length > 0 ? (
                state.filteredProducts.map((product, index) => (
                  <tr key={index}>
                    <td>{product.PRODUCT_NAME}</td>
                    <td>{product.PRODUCT_CODE}</td>
                    <td>{product.TURN}</td>
                    <td>{product.PRICE}</td>
                    <td>{product.TOTAL_PRICE}</td>
                    <td>
                      <IPButton
                        text='Sepete Ekle'
                        onClick={() => handleAddToBasket(product)}
                        disabled={!product.TOTAL_PRICE}
                        title={!product.TOTAL_PRICE ? 'Bu ürün için fiyat bulunmuyor' : undefined}
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className='text-center text-muted'>
                    {state.minUser ? 'Ürün bulunamadı' : 'Lütfen kullanıcı sayısı giriniz'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(KasperskyPage)
